import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Edit2, Plus, Trash2, CheckCircle, Users, Search, ChevronUp, Package } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WORKER_ROLES = ['operator', 'helper', 'supervisor', 'technician', 'qa_inspector'];

export default function UnifiedJCEditDialog({
  open, onOpenChange, workOrder, process, job, onSuccess,
  employees, shopfloorEmployees, items
}) {
  const { getAuthHeader } = useAuth();
  const isReceived = job?.status === 'completed';
  const isIssued = job?.status === 'issued';

  // === Common fields ===
  const [workers, setWorkers] = useState([]);
  const [machine, setMachine] = useState('');
  const [remarks, setRemarks] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [saving, setSaving] = useState(false);

  // === Issued-specific ===
  const [qtyToProduce, setQtyToProduce] = useState(0);
  const [materials, setMaterials] = useState([]);
  const [additionalMaterials, setAdditionalMaterials] = useState([]);

  // === Received-specific ===
  const [qtyCompleted, setQtyCompleted] = useState(0);
  const [defectQty, setDefectQty] = useState(0);
  const [hours, setHours] = useState(0);
  const [laborEntries, setLaborEntries] = useState([]);
  const [subProcesses, setSubProcesses] = useState([]);

  // === Fix Bug 11c — OJC-specific state ===
  const isOjc = Boolean(job?.is_outsourced || job?.ojc_id);
  const [ojcDoc, setOjcDoc] = useState(null);
  const [ojcJobberId, setOjcJobberId] = useState('');
  const [ojcJobberName, setOjcJobberName] = useState('');
  const [ojcExpectedRate, setOjcExpectedRate] = useState(0);
  const [suppliers, setSuppliers] = useState([]);

  // Bug B — canonical unit map indexed by item_id. Populated from the
  // items prop when the dialog opens. Display always reads from this map,
  // never from the stored job.materials_issued[].unit (which is legacy and
  // may carry 'NOS' drift). If an item is missing from the map, the cell
  // renders '—' and logs a warning — no silent 'NOS' fallback.
  const itemUnitById = {};
  (items || []).forEach(it => {
    if (it && it.id) itemUnitById[it.id] = (it.unit || '');
  });
  const resolveUnit = (m) => {
    const master = m.item_id ? itemUnitById[m.item_id] : null;
    if (master) return master;
    if (m.item_id && typeof console !== 'undefined') {
      console.warn(`UnifiedJCEditDialog: no unit in item master for item_id=${m.item_id}`);
    }
    return '—';
  };

  // === BPD state (received only) ===
  const [existingBpds, setExistingBpds] = useState([]); // snapshotted from WO
  const [deletedBpdIndices, setDeletedBpdIndices] = useState(new Set());
  const [editedBpds, setEditedBpds] = useState({}); // { originalIndex: { qty, price } }
  const [newBpds, setNewBpds] = useState([]);

  // Worker search state for labor entry popover
  const [workerSearch, setWorkerSearch] = useState('');
  const [workerDropdownOpen, setWorkerDropdownOpen] = useState(null);

  // Initialize form when dialog opens
  useEffect(() => {
    if (!open || !job || !workOrder) return;

    // Common
    setWorkers(job.workers?.map(w => ({
      employee_id: w.employee_id || w.worker_id || '',
      employee_name: w.employee_name || w.worker_name || '',
      role: w.role || 'operator'
    })) || [{ employee_id: '', employee_name: '', role: 'operator' }]);
    setMachine(job.machine || '');
    setRemarks(job.remarks || '');
    setCorrectionReason('');
    setSaving(false);

    if (isIssued) {
      setQtyToProduce(job.quantity_issued || 0);
      setMaterials((job.materials_issued || []).map(m => ({
        item_id: m.item_id || '', item_name: m.item_name || '',
        new_quantity: m.quantity_issued || 0,
        // Bug B: pull unit from item master at open time. Never fall back to 'NOS'.
        unit: (m.item_id && itemUnitById[m.item_id]) || ''
      })));
      setAdditionalMaterials([]);
    }

    if (isReceived) {
      setQtyCompleted(job.quantity_completed || 0);
      setDefectQty(job.quantity_defects || job.defect_quantity || 0);
      setHours(job.actual_time_hours || 0);
      setLaborEntries((job.labor_entries || []).map(le => ({
        employee_id: le.employee_id || '', employee_name: le.employee_name || '',
        sub_process_name: le.sub_process_name || '',
        normal_hours: le.normal_hours || 0, overtime_hours: le.overtime_hours || 0,
        hourly_rate: le.hourly_rate || 0, ot_rate: le.ot_rate || 0
      })));
      // Snapshot BPDs linked to this job
      const jobBpds = (workOrder.bpd_items || []).map((b, idx) => ({ ...b, _origIdx: idx }))
        .filter(b => b.job_id === job.id);
      setExistingBpds(jobBpds);
      setDeletedBpdIndices(new Set());
      setEditedBpds({});
      setNewBpds([]);
      // Fetch sub-processes for labor dropdown
      fetchSubProcesses();
    }

    // Fix Bug 11c — OJC: fetch register doc + suppliers when editing OJC job.
    if (isOjc && job.ojc_id) {
      (async () => {
        try {
          const [ojcResp, supResp] = await Promise.all([
            axios.get(`${API_URL}/ojc/${job.ojc_id}`, { headers: getAuthHeader() }),
            axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
          ]);
          setOjcDoc(ojcResp.data);
          setOjcJobberId(ojcResp.data?.jobber_id || '');
          setOjcJobberName(ojcResp.data?.jobber_name || '');
          setOjcExpectedRate(ojcResp.data?.expected_rate_per_unit ?? 0);
          setSuppliers(Array.isArray(supResp.data) ? supResp.data : (supResp.data?.data || []));
        } catch (e) {
          // degrade gracefully — section will be disabled
          setOjcDoc(null);
        }
      })();
    } else {
      setOjcDoc(null);
      setOjcJobberId('');
      setOjcJobberName('');
      setOjcExpectedRate(0);
    }
  }, [open, job?.id]);

  const fetchSubProcesses = async () => {
    if (!process?.process_name) return;
    try {
      const r = await axios.get(`${API_URL}/processes`, { headers: getAuthHeader() });
      const p = r.data.find(proc => proc.name === process.process_name);
      setSubProcesses(p?.sub_processes || []);
    } catch { setSubProcesses([]); }
  };

  // === No-change detection ===
  const hasJCChanges = () => {
    if (isIssued) {
      if (qtyToProduce !== (job.quantity_issued || 0)) return true;
      if (machine !== (job.machine || '')) return true;
      if (remarks !== (job.remarks || '')) return true;
      if (additionalMaterials.some(m => m.item_id && m.quantity_to_issue > 0)) return true;
      const origMats = job.materials_issued || [];
      if (materials.some(m => {
        const orig = origMats.find(om => om.item_id === m.item_id);
        return orig && Math.abs(m.new_quantity - (orig.quantity_issued || 0)) > 0.001;
      })) return true;
    }
    if (isReceived) {
      if (qtyCompleted !== (job.quantity_completed || 0)) return true;
      if (defectQty !== (job.quantity_defects || job.defect_quantity || 0)) return true;
      if (hours !== (job.actual_time_hours || 0)) return true;
      if (machine !== (job.machine || '')) return true;
      if (remarks !== (job.remarks || '')) return true;
    }
    return false;
  };

  const hasBpdChanges = () => {
    if (deletedBpdIndices.size > 0) return true;
    if (Object.keys(editedBpds).length > 0) return true;
    if (newBpds.some(b => (b.item_id || b.item_name) && b.quantity_received > 0)) return true;
    return false;
  };

  // Fix Bug 11c — detect jobber/rate changes vs currently-persisted OJC doc.
  const hasOjcJobberChanges = () => {
    if (!isOjc || !ojcDoc) return false;
    if ((ojcJobberId || '') !== (ojcDoc.jobber_id || '')) return true;
    if ((ojcJobberName || '') !== (ojcDoc.jobber_name || '')) return true;
    const curRate = ojcDoc.expected_rate_per_unit ?? 0;
    if (Number(ojcExpectedRate || 0) !== Number(curRate || 0)) return true;
    return false;
  };

  // === Save handler ===
  const handleSave = async () => {
    if (!correctionReason.trim()) { toast.error('Correction reason is mandatory'); return; }
    if (!hasJCChanges() && !hasBpdChanges() && !hasOjcJobberChanges()) {
      toast.info('No changes made');
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      // Step 1: Save JC edit (only if JC fields changed)
      if (hasJCChanges()) {
        if (isIssued) await saveIssuedJC();
        if (isReceived) await saveReceivedJC();
      }

      // Step 2: Process BPD operations (received only, only if BPD changed)
      if (isReceived && hasBpdChanges()) await processBpdOperations();

      // Step 3 (Fix Bug 11c): OJC jobber/rate — PATCH /api/ojc/{ojc_id}.
      // Cascades to process.jobber_id / jobber_name / ojc_rate_per_unit (option A).
      if (hasOjcJobberChanges() && job?.ojc_id) {
        await axios.patch(
          `${API_URL}/ojc/${job.ojc_id}`,
          {
            jobber_id: ojcJobberId || null,
            jobber_name: ojcJobberName || null,
            expected_rate_per_unit: Number(ojcExpectedRate) || 0,
          },
          { headers: getAuthHeader() }
        );
      }

      toast.success(`Job ${job.job_letter} updated`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveIssuedJC = async () => {
    const validWorkers = workers.filter(w => w.employee_id);
    const payload = {
      quantity_to_produce: qtyToProduce,
      materials: materials.filter(m => m.item_id).map(m => ({
        item_id: m.item_id, item_name: m.item_name, new_quantity: m.new_quantity,
        // Bug B: resolve unit from item master at submit time; never default to 'NOS'.
        unit: (m.item_id && itemUnitById[m.item_id]) || m.unit || ''
      })),
      workers: validWorkers,
      machine, remarks,
      correction_reason: correctionReason.trim()
    };
    await axios.patch(
      `${API_URL}/work-orders/${workOrder.id}/processes/${process.id}/jobs/${job.id}/edit-issued`,
      payload, { headers: getAuthHeader() }
    );
    // Additional materials
    const validExtra = additionalMaterials.filter(m => m.item_id && m.quantity_to_issue > 0);
    if (validExtra.length > 0) {
      await axios.post(
        `${API_URL}/work-orders/${workOrder.id}/jobs/${job.id}/add-materials`,
        { materials: validExtra.map(m => ({ item_id: m.item_id, item_name: m.item_name, quantity_to_issue: m.quantity_to_issue, unit: m.unit || 'PCS' })) },
        { headers: getAuthHeader() }
      );
    }
  };

  const saveReceivedJC = async () => {
    const validLabor = laborEntries.filter(en => en.employee_id && en.sub_process_name && (en.normal_hours > 0 || en.overtime_hours > 0));
    const validWorkers = workers.filter(w => w.employee_id);
    const payload = {
      quantity_completed: qtyCompleted,
      defect_quantity: defectQty,
      actual_time_hours: hours,
      labor_entries: validLabor.map(le => ({
        employee_id: le.employee_id, employee_name: le.employee_name,
        sub_process_name: le.sub_process_name,
        normal_hours: le.normal_hours, overtime_hours: le.overtime_hours,
        output_qty_attributed: 0
      })),
      workers: validWorkers,
      machine, remarks,
      correction_reason: correctionReason.trim()
    };
    await axios.patch(
      `${API_URL}/work-orders/${workOrder.id}/processes/${process.id}/jobs/${job.id}/edit`,
      payload, { headers: getAuthHeader() }
    );
  };

  const processBpdOperations = async () => {
    const woId = workOrder.id;
    // Helper to format a server-side detail into a readable string.
    const detailText = (e, fallback) => {
      const d = e?.response?.data?.detail;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(', ');
      return fallback;
    };
    // 1. Deletes (reverse order to preserve indices)
    const deleteList = [...deletedBpdIndices].sort((a, b) => b - a);
    for (const origIdx of deleteList) {
      try {
        await axios.delete(`${API_URL}/work-orders/${woId}/bpd/${origIdx}`, { headers: getAuthHeader() });
      } catch (e) {
        // Surface the error and re-throw so handleSave keeps the dialog open
        // and the operator is not misled into thinking the save succeeded.
        toast.error(`Failed to delete BPD item: ${detailText(e, 'error')}`);
        throw e;
      }
    }
    // 2. Edits (shift indices to account for prior deletes)
    for (const [origIdxStr, changes] of Object.entries(editedBpds)) {
      const origIdx = parseInt(origIdxStr);
      if (deletedBpdIndices.has(origIdx)) continue;
      const belowDeletes = deleteList.filter(d => d < origIdx).length;
      const shiftedIdx = origIdx - belowDeletes;
      try {
        await axios.put(`${API_URL}/work-orders/${woId}/bpd/${shiftedIdx}`, {
          quantity_received: changes.qty, price: changes.price
        }, { headers: getAuthHeader() });
      } catch (e) {
        toast.error(`Failed to update BPD item: ${detailText(e, 'error')}`);
        throw e;
      }
    }
    // 3. Adds (new BPD items) — backend expects a bare list payload
    const validNew = newBpds.filter(b => (b.item_id || b.item_name) && b.quantity_received > 0);
    if (validNew.length > 0) {
      try {
        await axios.post(
          `${API_URL}/work-orders/${woId}/bpd-multiple`,
          validNew.map(b => ({
            item_id: b.item_id, item_code: b.item_code || '', item_name: b.item_name,
            quantity_received: b.quantity_received, price: b.price || 0,
            unit: b.unit || 'NOS', bpd_type: b.bpd_type || 'by_product',
            process_id: process?.id, process_name: process?.process_name,
            job_id: job?.id, job_number: job?.job_number
          })),
          { headers: getAuthHeader() }
        );
      } catch (e) {
        toast.error(`Failed to add BPD items: ${detailText(e, 'error')}`);
        throw e;
      }
    }
  };

  // === Material auto-recalc (issued) ===
  const handleQtyToProduceChange = (newQty) => {
    const oldQty = job?.quantity_issued || 0;
    const newMaterials = materials.map(m => {
      const origMatQty = (job?.materials_issued || []).find(om => om.item_id === m.item_id)?.quantity_issued || 0;
      const ratio = oldQty > 0 ? origMatQty / oldQty : 0;
      return { ...m, new_quantity: Math.round(newQty * ratio * 10000) / 10000 };
    });
    setQtyToProduce(newQty);
    setMaterials(newMaterials);
  };

  // === BPD helpers ===
  const markBpdEdited = (origIdx, field, value) => {
    const bpd = existingBpds.find(b => b._origIdx === origIdx);
    if (!bpd) return;
    const current = editedBpds[origIdx] || {
      qty: bpd.quantity_received || bpd.quantity || 0,
      price: bpd.price || bpd.rate || 0
    };
    setEditedBpds({ ...editedBpds, [origIdx]: { ...current, [field]: value } });
  };

  const getBpdDisplayValue = (bpd, field) => {
    const edited = editedBpds[bpd._origIdx];
    if (edited) return field === 'qty' ? edited.qty : edited.price;
    return field === 'qty'
      ? (bpd.quantity_received || bpd.quantity || 0)
      : (bpd.price || bpd.rate || 0);
  };

  const addNewBpd = () => {
    setNewBpds([...newBpds, { item_id: '', item_code: '', item_name: '', quantity_received: 0, price: 0, unit: 'NOS', bpd_type: 'by_product' }]);
  };

  const updateNewBpd = (idx, field, value) => {
    // Use functional setter to avoid stale-closure race when the caller fires
    // multiple sequential updateNewBpd calls inside one event handler (e.g.
    // picking an item populates item_id, item_name, item_code, and unit).
    setNewBpds(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  if (!job || !workOrder || !process) return null;

  const visibleExistingBpds = existingBpds.filter(b => !deletedBpdIndices.has(b._origIdx));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="unified-jc-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className={`w-5 h-5 ${isIssued ? 'text-amber-600' : 'text-blue-600'}`} />
            Edit Job Card
            <Badge variant={isIssued ? 'outline' : 'default'} className={isIssued ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
              {isIssued ? 'Issued' : 'Received'}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Job {job.job_letter} ({job.job_number}) — {workOrder.work_order_number} / {process.process_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Info Banner */}
          <div className="p-3 bg-muted rounded-lg text-sm space-y-1" data-testid="unified-edit-info">
            <p><strong>Job:</strong> {job.job_letter} ({job.job_number})</p>
            <p><strong>Issued Qty:</strong> {job.quantity_issued || 0}</p>
            {isReceived && <p><strong>Completed:</strong> {job.quantity_completed || 0}</p>}
            {isOjc && <p><strong>OJC:</strong> {job.ojc_number || (ojcDoc?.ojc_number) || '-'}</p>}
          </div>

          {/* ============= OJC JOBBER & RATE SECTION (Fix Bug 11c) ============= */}
          {isOjc && (
            <div className="border rounded-lg p-4 space-y-3 bg-orange-50/30" data-testid="unified-edit-ojc-section">
              <Label className="text-base font-semibold">Jobber & Rate</Label>
              {!ojcDoc ? (
                <p className="text-sm text-muted-foreground">Loading OJC details…</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Jobber</Label>
                      <Select
                        value={ojcJobberId || ''}
                        onValueChange={(v) => {
                          const s = suppliers.find(x => x.id === v);
                          setOjcJobberId(v);
                          setOjcJobberName(s?.name || '');
                        }}
                      >
                        <SelectTrigger data-testid="unified-edit-ojc-jobber-select">
                          <SelectValue placeholder="Select jobber" />
                        </SelectTrigger>
                        <SelectContent>
                          {(suppliers || []).map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Expected Rate / Unit</Label>
                      <Input
                        data-testid="unified-edit-ojc-rate"
                        type="number" step="0.01" min="0"
                        value={ojcExpectedRate ?? 0}
                        onChange={(e) => setOjcExpectedRate(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Editing these fields updates the OJC register AND cascades to the WO process (both stay in sync).
                  </div>

                  {/* Received Batches — read-only summary */}
                  {(ojcDoc.receiving_batches || []).length > 0 && (
                    <div className="border-t pt-3 space-y-2">
                      <Label className="text-sm font-semibold">Received Batches (read-only)</Label>
                      <div className="border rounded overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="w-[50px]">#</TableHead>
                              <TableHead className="text-right">Received</TableHead>
                              <TableHead className="text-right">Accepted</TableHead>
                              <TableHead className="text-right">Labour Charges</TableHead>
                              <TableHead className="text-right">Other Charges</TableHead>
                              <TableHead>Received At</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(ojcDoc.receiving_batches || []).map((b, idx) => (
                              <TableRow key={idx} data-testid={`unified-edit-ojc-batch-${idx}`}>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell className="text-right">{b.quantity_received ?? '-'}</TableCell>
                                <TableCell className="text-right">{b.quantity_accepted ?? '-'}</TableCell>
                                <TableCell className="text-right">{formatCurrency(b.labour_charges || 0)}</TableCell>
                                <TableCell className="text-right">{formatCurrency(b.other_charges || 0)}</TableCell>
                                <TableCell className="text-xs">{b.received_at ? String(b.received_at).split('T')[0] : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Per-batch labour/other edits are available on the OJC Register page.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ============= ISSUED JC FIELDS ============= */}
          {isIssued && (
            <>
              {/* Qty to Produce */}
              <div className="space-y-2">
                <Label>Quantity to Produce *</Label>
                <Input data-testid="unified-edit-qty-produce" type="number" step="0.01"
                  min={job.quantity_completed || 0} max={workOrder.quantity || 999999}
                  value={qtyToProduce} onChange={(e) => handleQtyToProduceChange(parseFloat(e.target.value) || 0)} />
                {(job.quantity_completed || 0) > 0 && (
                  <p className="text-xs text-amber-600">Cannot reduce below completed: {job.quantity_completed}</p>
                )}
              </div>

              {/* Materials Table */}
              {materials.length > 0 && (
                <div className="border-t pt-4 space-y-2">
                  <Label className="text-base font-semibold">Materials (Issued Quantities)</Label>
                  <p className="text-xs text-muted-foreground">Auto-adjusted when quantity changes. Override manually if needed.</p>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader><TableRow className="bg-muted/50"><TableHead>Material</TableHead><TableHead className="w-[100px] text-right">Original</TableHead><TableHead className="w-[150px] text-right">New Qty</TableHead><TableHead className="w-[60px]">Unit</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {materials.map((mat, idx) => {
                          const origQty = (job.materials_issued || []).find(om => om.item_id === mat.item_id)?.quantity_issued || 0;
                          const changed = Math.abs(mat.new_quantity - origQty) > 0.001;
                          return (
                            <TableRow key={idx} className={changed ? 'bg-amber-50/50' : ''}>
                              <TableCell className="text-sm">{mat.item_name}</TableCell>
                              <TableCell className="text-right text-sm text-muted-foreground">{origQty}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 justify-end">
                                  <Input data-testid={`unified-edit-mat-${idx}`} type="text" inputMode="decimal" className="h-8 w-20 text-right"
                                    value={mat.new_quantity || ''} onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                        const n = [...materials]; n[idx] = { ...n[idx], new_quantity: val === '' ? 0 : parseFloat(val) || 0 };
                                        setMaterials(n);
                                      }
                                    }} />
                                  <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" title="Round up"
                                    onClick={() => { const n = [...materials]; n[idx] = { ...n[idx], new_quantity: Math.ceil(mat.new_quantity || 0) }; setMaterials(n); }}>
                                    <ChevronUp className="w-4 h-4 text-blue-600" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">{mat.unit}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Additional Materials */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <Label className="font-semibold">Add Extra Materials</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="unified-edit-add-extra-mat"
                    onClick={() => setAdditionalMaterials([...additionalMaterials, { item_id: '', item_name: '', quantity_to_issue: 0, unit: 'PCS' }])}>
                    <Plus className="w-3 h-3 mr-1" />Add
                  </Button>
                </div>
                {additionalMaterials.map((m, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-amber-50/50 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <SearchableSelect
                          options={[...items].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(i => ({
                            value: i.id, label: `${i.name} — Stock: ${i.stock_in_hand || 0}`,
                            code: i.item_code || 'N/A', category: i.category || '',
                            searchTerms: [i.name || '', i.item_code || '']
                          }))}
                          value={m.item_id}
                          onValueChange={(v) => {
                            const item = items.find(i => i.id === v);
                            const updated = [...additionalMaterials];
                            updated[idx] = { ...updated[idx], item_id: v, item_name: item?.name || '', unit: item?.unit || '' };
                            setAdditionalMaterials(updated);
                          }}
                          placeholder="Search item..."
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <Input type="number" step="0.01" min="0" className="w-20" value={m.quantity_to_issue}
                          onChange={(e) => { const u = [...additionalMaterials]; u[idx] = { ...u[idx], quantity_to_issue: parseFloat(e.target.value) || 0 }; setAdditionalMaterials(u); }} />
                        <Button type="button" size="sm" variant="ghost" onClick={() => setAdditionalMaterials(additionalMaterials.filter((_, i) => i !== idx))}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ============= RECEIVED JC FIELDS ============= */}
          {isReceived && (
            <>
              {/* Completed Qty */}
              <div className="space-y-2">
                <Label>Completed Quantity *</Label>
                <Input data-testid="unified-edit-qty-completed" type="number" step="0.01" min="0" max={job.quantity_issued || 999999}
                  value={qtyCompleted} onChange={(e) => setQtyCompleted(parseFloat(e.target.value) || 0)} />
              </div>

              {/* Hours + Defects */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Actual Time (Hours)</Label>
                  <Input data-testid="unified-edit-hours" type="text" inputMode="decimal"
                    value={hours || ''} onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d*$/.test(val)) setHours(val === '' ? 0 : parseFloat(val) || 0);
                    }} />
                </div>
                <div className="space-y-2">
                  <Label>Defect Quantity</Label>
                  <Input data-testid="unified-edit-defects" type="number" step="0.01" min="0"
                    value={defectQty} onChange={(e) => setDefectQty(parseFloat(e.target.value) || 0)} />
                </div>
              </div>

              {/* Labor Details */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold flex items-center gap-2"><Users className="w-4 h-4" />Labor Details ({laborEntries.length})</Label>
                  <Button type="button" variant="outline" size="sm" data-testid="unified-edit-add-labor"
                    onClick={() => setLaborEntries([...laborEntries, { employee_id: '', employee_name: '', sub_process_name: '', normal_hours: 0, overtime_hours: 0, hourly_rate: 0, ot_rate: 0 }])}>
                    <Plus className="w-4 h-4 mr-1" />Add
                  </Button>
                </div>
                {laborEntries.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="w-[180px]">Worker</TableHead>
                          <TableHead className="w-[150px]">Sub-Process *</TableHead>
                          <TableHead className="w-[80px] text-right">Norm Hrs</TableHead>
                          <TableHead className="w-[80px] text-right">OT Hrs</TableHead>
                          <TableHead className="w-[80px] text-right">Rate</TableHead>
                          <TableHead className="w-[90px] text-right">Total</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {laborEntries.map((entry, idx) => {
                          const normalCost = (entry.normal_hours || 0) * (entry.hourly_rate || 0);
                          const otCost = (entry.overtime_hours || 0) * (entry.ot_rate || 0);
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <Popover open={workerDropdownOpen === `labor-${idx}`} onOpenChange={(o) => { setWorkerDropdownOpen(o ? `labor-${idx}` : null); if (o) setWorkerSearch(''); }}>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" className="h-8 w-full justify-between text-left font-normal">
                                      {entry.employee_id ? (shopfloorEmployees.find(e => e.id === entry.employee_id)?.name || entry.employee_name || 'Select...') : 'Select...'}
                                      <Search className="ml-1 h-3 w-3 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[220px] p-0" align="start">
                                    <Command shouldFilter={false}>
                                      <CommandInput placeholder="Search workers..." value={workerSearch} onValueChange={setWorkerSearch} />
                                      <CommandList>
                                        <CommandEmpty>No worker found.</CommandEmpty>
                                        <CommandGroup>
                                          {shopfloorEmployees
                                            .filter(e => !workerSearch || e.name?.toLowerCase().includes(workerSearch.toLowerCase()))
                                            .map(e => (
                                              <CommandItem key={e.id} value={e.id} onSelect={() => {
                                                const n = [...laborEntries]; n[idx] = { ...n[idx], employee_id: e.id, employee_name: e.name, hourly_rate: e.hourly_rate || 0, ot_rate: e.ot_rate || 0 };
                                                setLaborEntries(n); setWorkerDropdownOpen(null);
                                              }}>
                                                {e.name} ({formatCurrency(e.hourly_rate)}/hr)
                                              </CommandItem>
                                            ))}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              </TableCell>
                              <TableCell>
                                <Select value={entry.sub_process_name} onValueChange={(val) => { const n = [...laborEntries]; n[idx] = { ...n[idx], sub_process_name: val }; setLaborEntries(n); }}>
                                  <SelectTrigger className="h-8"><SelectValue placeholder="Select..." /></SelectTrigger>
                                  <SelectContent>{subProcesses.length > 0 ? subProcesses.map((sp, i) => <SelectItem key={i} value={sp.name}>{sp.name}</SelectItem>) : <SelectItem value="General">General</SelectItem>}</SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell><Input type="text" inputMode="decimal" className="h-8 w-16 text-right" value={entry.normal_hours || ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) { const n = [...laborEntries]; n[idx] = { ...n[idx], normal_hours: val === '' ? 0 : parseFloat(val) || 0 }; setLaborEntries(n); } }} /></TableCell>
                              <TableCell><Input type="text" inputMode="decimal" className="h-8 w-16 text-right" value={entry.overtime_hours || ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) { const n = [...laborEntries]; n[idx] = { ...n[idx], overtime_hours: val === '' ? 0 : parseFloat(val) || 0 }; setLaborEntries(n); } }} /></TableCell>
                              <TableCell className="text-right text-sm text-muted-foreground">{formatCurrency(entry.hourly_rate || 0)}/hr</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(normalCost + otCost)}</TableCell>
                              <TableCell>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setLaborEntries(laborEntries.filter((_, i) => i !== idx))}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ============= WORKERS (common) ============= */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between">
              <Label className="font-semibold">Workers</Label>
              <Button type="button" size="sm" variant="outline" data-testid="unified-edit-add-worker"
                onClick={() => { if (workers.length >= 5) { toast.error('Maximum 5 workers'); return; } setWorkers([...workers, { employee_id: '', employee_name: '', role: 'operator' }]); }}
                disabled={workers.length >= 5}>
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
            {workers.map((w, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Select value={w.employee_id} onValueChange={(v) => {
                  const emp = employees.find(e => e.id === v);
                  const n = [...workers]; n[idx] = { ...n[idx], employee_id: v, employee_name: emp?.name || '' }; setWorkers(n);
                }}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={w.role} onValueChange={(v) => { const n = [...workers]; n[idx] = { ...n[idx], role: v }; setWorkers(n); }}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>{WORKER_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
                {workers.length > 1 && (
                  <Button type="button" size="sm" variant="ghost" onClick={() => setWorkers(workers.filter((_, i) => i !== idx))}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Machine */}
          <div className="space-y-2">
            <Label>Machine</Label>
            <Input data-testid="unified-edit-machine" value={machine} onChange={(e) => setMachine(e.target.value)} />
          </div>

          {/* Remarks */}
          <div className="space-y-2">
            <Label>Remarks</Label>
            <Textarea data-testid="unified-edit-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} />
          </div>

          {/* ============= BPD SECTION (received only) ============= */}
          {isReceived && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4" />By-Products / Material Returns ({visibleExistingBpds.length + newBpds.length})
                </Label>
                <Button type="button" variant="outline" size="sm" data-testid="unified-edit-add-bpd" onClick={addNewBpd}>
                  <Plus className="w-4 h-4 mr-1" />Add BPD
                </Button>
              </div>

              {/* Existing BPDs */}
              {visibleExistingBpds.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50/50">
                        <TableHead>Item</TableHead>
                        <TableHead className="w-[100px] text-right">Qty</TableHead>
                        <TableHead className="w-[100px] text-right">Price</TableHead>
                        <TableHead className="w-[100px] text-right">Amount</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleExistingBpds.map((bpd) => {
                        const qty = getBpdDisplayValue(bpd, 'qty');
                        const price = getBpdDisplayValue(bpd, 'price');
                        const isEdited = editedBpds[bpd._origIdx] !== undefined;
                        return (
                          <TableRow key={bpd._origIdx} className={isEdited ? 'bg-amber-50/30' : ''}>
                            <TableCell className="text-sm">
                              {bpd.item_name}
                              {bpd.bpd_type === 'material_return' && <Badge variant="outline" className="ml-2 text-xs bg-blue-50 text-blue-600">Return</Badge>}
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.01" min="0" className="h-8 w-20 text-right"
                                data-testid={`unified-bpd-qty-${bpd._origIdx}`}
                                value={qty} onChange={(e) => markBpdEdited(bpd._origIdx, 'qty', parseFloat(e.target.value) || 0)} />
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.01" min="0" className="h-8 w-20 text-right"
                                data-testid={`unified-bpd-price-${bpd._origIdx}`}
                                value={price} onChange={(e) => markBpdEdited(bpd._origIdx, 'price', parseFloat(e.target.value) || 0)} />
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-emerald-600">{formatCurrency(qty * price)}</TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="sm"
                                data-testid={`unified-bpd-delete-${bpd._origIdx}`}
                                onClick={() => { setDeletedBpdIndices(new Set([...deletedBpdIndices, bpd._origIdx])); }}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* New BPDs */}
              {newBpds.map((bpd, idx) => (
                <div key={`new-${idx}`} className="border rounded-lg p-3 bg-emerald-50/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-700 text-xs">New BPD</Badge>
                    <div className="flex items-center gap-2">
                      <Select value={bpd.bpd_type} onValueChange={(v) => updateNewBpd(idx, 'bpd_type', v)}>
                        <SelectTrigger className="h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="by_product">By-Product</SelectItem>
                          <SelectItem value="material_return">Material Return</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setNewBpds(newBpds.filter((_, i) => i !== idx))}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <SearchableSelect
                        options={[...items].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(i => ({
                          value: i.id, label: `${i.name} — Stock: ${i.stock_in_hand || 0}`,
                          code: i.item_code || 'N/A', category: i.category || '',
                          searchTerms: [i.name || '', i.item_code || '']
                        }))}
                        value={bpd.item_id}
                        onValueChange={(v) => {
                          const item = items.find(i => i.id === v);
                          updateNewBpd(idx, 'item_id', v);
                          updateNewBpd(idx, 'item_name', item?.name || '');
                          updateNewBpd(idx, 'item_code', item?.item_code || '');
                          updateNewBpd(idx, 'unit', item?.unit || '');
                        }}
                        placeholder="Search item..."
                      />
                    </div>
                    <Input type="number" step="0.01" min="0" placeholder="Qty"
                      data-testid={`unified-new-bpd-qty-${idx}`}
                      value={bpd.quantity_received || ''} onChange={(e) => updateNewBpd(idx, 'quantity_received', parseFloat(e.target.value) || 0)} />
                    <Input type="number" step="0.01" min="0" placeholder="Price"
                      data-testid={`unified-new-bpd-price-${idx}`}
                      value={bpd.price || ''} onChange={(e) => updateNewBpd(idx, 'price', parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Correction Reason (MANDATORY) */}
          <div className="border-t pt-4 space-y-2">
            <Label className="text-red-600 font-semibold">Correction Reason *</Label>
            <Textarea data-testid="unified-edit-reason" value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)} rows={2}
              placeholder="Why is this correction being made?" required />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="unified-edit-submit" onClick={handleSave}
            disabled={saving || !correctionReason.trim() || (isIssued && qtyToProduce <= 0) || (isReceived && qtyCompleted <= 0)}>
            <CheckCircle className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
